import { useCallback, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import { useResetPasswordMutation } from "@/features/auth/api";
import { logoutUser } from "@/lib/authHelpers";
import { useSession } from "@/lib/session";
import { setLoginContext } from "@/store/slices/authSlice";
import type { RootState } from "@/store/store";
import { extractApiError } from "@/shared/lib/apiError";
import { Alert, AlertDescription } from "@/shared/ui/alert/Alert";
import { Button } from "@/shared/ui/button/Button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/ui/form/Form";
import { Input } from "@/shared/ui/input/Input";

import { AuthCard, AuthShell } from "./AuthShell";

/** How long the confirmation stays up before the user is sent to sign in again. */
const SIGN_OUT_DELAY_MS = 1200;

const schema = z
  .object({
    oldPassword: z.string(),
    newPassword: z.string().min(8, "New password must be at least 8 characters."),
    confirmPassword: z.string(),
  })
  .refine((v) => v.newPassword === v.confirmPassword, {
    path: ["confirmPassword"],
    message: "Passwords do not match.",
  });

type FormValues = z.infer<typeof schema>;

/** Case-insensitive ADMIN check (JWT role → ROLE_ADMIN). */
function isAdminRole(role?: string | null): boolean {
  const normalized = role?.trim().toLowerCase();
  return normalized === "admin" || normalized === "role_admin" || normalized === "super_admin";
}

/**
 * Authenticated password change.
 *
 * Doubles as the forced-change screen: an account created by an administrator
 * carries `requirePasswordChange` until the holder picks their own password,
 * and the server refuses all user administration from it until then. When the
 * change is mandatory there is deliberately no way out of this screen.
 */
export function ResetPasswordView() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { data: session } = useSession();
  const [resetPassword, { isLoading }] = useResetPasswordMutation();
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const authUsername = useSelector((s: RootState) => s.auth.user?.username);
  const mustChange = useSelector((s: RootState) => s.auth.user?.requirePasswordChange === true);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { oldPassword: "", newPassword: "", confirmPassword: "" },
  });

  // Prefer the bare username from `/auth/me`. The session carries the JWT
  // subject (`username-MERCHANT`); never split it here, which would truncate
  // usernames that legitimately contain a hyphen.
  const username = authUsername ?? session?.user?.username ?? session?.user?.name;
  const merchantId = session?.user?.merchantId;
  const admin = isAdminRole(session?.user?.role);

  const onSubmit = useCallback(
    async (values: FormValues) => {
      setError(null);
      if (!username || !merchantId) {
        setError("Your session is missing a username or merchant. Please sign in again.");
        return;
      }
      if (!admin && !values.oldPassword.trim()) {
        form.setError("oldPassword", { message: "Current password is required." });
        return;
      }

      try {
        await resetPassword({
          userName: username,
          merchantId,
          oldPassword: values.oldPassword.trim() || undefined,
          newPassword: values.newPassword,
        }).unwrap();

        // Choosing your own password clears the server's flag; mirror it so
        // the shell stops sending the user back here.
        dispatch(setLoginContext({ requirePasswordChange: false }));
        setDone(true);
        // Sign in again rather than carrying on under the session opened with
        // the old password — logging out blocklists that token, so the
        // credentials just replaced cannot be reused by anything holding it.
        window.setTimeout(() => void logoutUser(undefined, "/login"), SIGN_OUT_DELAY_MS);
      } catch (err) {
        const httpStatus = (err as { status?: number } | undefined)?.status;
        if (httpStatus === 401) {
          setError("Authentication required. Please sign in again.");
          return;
        }
        if (httpStatus === 403) {
          setError("Access denied. You can only reset your own password.");
          return;
        }
        setError(extractApiError(err, "Password reset failed."));
      }
    },
    [admin, dispatch, form, merchantId, resetPassword, username]
  );

  if (!session) {
    return <Navigate to="/login" replace />;
  }

  return (
    <AuthShell>
      <AuthCard
        title={mustChange ? "Choose your password" : "Reset password"}
        subtitle={
          mustChange
            ? "Your password was set by an administrator. Choose your own before continuing."
            : "Enter your current password and choose a new one."
        }
      >
        {done ? (
          <p className="text-center text-sm text-success">
            Password updated. Taking you to sign in…
          </p>
        ) : (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4" noValidate>
              {!admin && (
                <FormField
                  control={form.control}
                  name="oldPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>Current password</FormLabel>
                      <FormControl>
                        <Input type="password" autoComplete="current-password" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
              <FormField
                control={form.control}
                name="newPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel required>New password</FormLabel>
                    <FormControl>
                      <Input type="password" autoComplete="new-password" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel required>Confirm new password</FormLabel>
                    <FormControl>
                      <Input type="password" autoComplete="new-password" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="flex justify-end gap-2">
                {/* No way out while the change is mandatory — going back only
                    bounces off the shell guard, and the server refuses user
                    administration until it is done. */}
                {!mustChange && (
                  <Button type="button" variant="outline" onClick={() => navigate(-1)}>
                    Cancel
                  </Button>
                )}
                <Button type="submit" isLoading={isLoading}>
                  {isLoading ? "Updating…" : "Update password"}
                </Button>
              </div>
            </form>
          </Form>
        )}
      </AuthCard>
    </AuthShell>
  );
}
